"use client";
import React, {
  type CSSProperties,
  useEffect,
  useState,
} from "react";
import dayjs from "dayjs";
import Button from "antd/es/button";
import Col from "antd/es/col";
import Flex from "antd/es/flex";
import Row from "antd/es/row";

import OpeningLoading from "../../components/OpeningLoading";
import { CalendarOutlined } from "@ant-design/icons";
import TimeButton from "../../components/TimeButton";
import type { DbDateString, DbTimeString } from "@openings/api-client";
import { formatCompactTime } from "@utils/core";
import { formatDate } from "../utils";
import type {
  BookingAppointmentForm,
  BookingOpeningsClient,
  BookingUser,
  SelectedService,
} from "../../types";
import {
  bookingPrimaryActionStyle,
  bookingSectionTitleStyle,
  bookingSmallStyle,
} from "../../bookingStyles";
import { TimeGrid } from "../../components/TimeGrid";
import Paragraph from "antd/es/typography/Paragraph";
import Text from "antd/es/typography/Text";

/**
 * How many dates the list shows. The API stops searching once it has them,
 * instead of reading all 90 days (#818).
 */
const NEXT_OPENINGS_DATE_LIMIT = 10;

const emptyStateTextStyle: CSSProperties = {
  ...bookingSmallStyle,
  margin: 0,
};

const availabilityPromptStyle: CSSProperties = {
  width: "100%",
  gap: "var(--openings-booking-spacing-md, 12px)",
};

interface DisplayNextOpeningsProps {
  salonId: string;
  user: {
    id: string;
    name: string;
    tags?: BookingUser["tags"];
  };
  teamMemberId?: string | null;
  scheduleIds: string[];
  duration: number;
  date: DbDateString;
  services: SelectedService[];
  onTimeClick: (args: BookingAppointmentForm) => void;
  openingsClient: BookingOpeningsClient;
  cancellationPolicyEnabled?: boolean;
  cancellationPolicyHours?: number | null;
  cancellationPolicyNote?: string | null;
  lateGraceMinutes?: number | null;
  verificationMethod?: "SMS" | "EMAIL";
  showAvailabilityStatus?: boolean;
}

const DisplayNextOpenings = ({
  salonId,
  user,
  teamMemberId,
  scheduleIds,
  date,
  duration,
  services,
  onTimeClick,
  openingsClient,
  cancellationPolicyEnabled,
  cancellationPolicyHours,
  cancellationPolicyNote,
  lateGraceMinutes,
  verificationMethod,
  showAvailabilityStatus = false,
}: DisplayNextOpeningsProps) => {
  const [isLoading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedOpening, setSelectedOpening] = useState<{
    scopeKey: string;
    time: DbTimeString;
  } | null>(null);
  const [data, setData] = useState<
    Array<{
      date: string;
      openings: string[];
      openingLabels?: Record<string, string>;
      timezone?: string;
      schedule: {
        id?: string;
        title?: string;
        address?: string | null;
        /** @deprecated Use title/address directly. */
        location?: { name?: string } | null;
      };
    }>
  >([]);
  const selectionContextKey = `${user.id}:${date}:${duration}:${scheduleIds.join(
    ",",
  )}:${services
    .map((service) => `${service.id}:${service.option?.id ?? ""}`)
    .join("|")}`;

  useEffect(() => {
    setSelectedOpening(null);
  }, [selectionContextKey]);

  const onSearch = async () => {
    let nextDate = date;
    if (dayjs(date).isSame(dayjs(), "day")) {
      nextDate = formatDate(dayjs(date).add(1, "day").toDate());
    }

    setLoading(true);
    try {
      const res = await openingsClient.getNextOpenings({
        salonId,
        scheduleIds,
        date: nextDate,
        duration,
        daysAhead: 90,
        limit: NEXT_OPENINGS_DATE_LIMIT,
        // The API leaves out the starts sooner than their minimum notice.
        serviceIds: services.map((service) => service.id),
      });
      setData(res.data ?? []);
    } catch (error) {
      console.error(error);
    }
    setSearched(true);
    setLoading(false);
  };

  if (isLoading) {
    return <OpeningLoading slotsOnly cards={1} />;
  }

  // Limit to first 10 unique dates
  const limitedData: typeof data = [];
  const seenDates = new Set<string>();
  for (const item of data) {
    if (seenDates.size >= NEXT_OPENINGS_DATE_LIMIT) break;
    seenDates.add(item.date);
    limitedData.push(item);
  }

  if (searched && limitedData.length === 0) {
    return (
      <Paragraph
        className="openings-booking-empty-message"
        style={emptyStateTextStyle}
      >
        No openings found in the next 3 months. Please contact this business
        directly to book.
      </Paragraph>
    );
  }

  return limitedData.length > 0 ? (
    <Flex vertical gap={20} style={{ width: "100%" }}>
      {limitedData.map((item, itemIndex) => {
        const dateLabel = dayjs(item.date).format("ddd, MMM D");
        const scheduleLabel =
          item.schedule.title ??
          item.schedule.address ??
          item.schedule.location?.name;
        const scheduleId = item.schedule.id ?? "";
        const scopeKey = `${user.id}:${item.date}:${scheduleId || itemIndex}`;
        const handleSelect = (time: DbTimeString) => {
          onTimeClick({
            salonId,
            userId: user.id,
            teamMemberId: teamMemberId ?? undefined,
            stylistName: user.name,
            memberTags: user.tags,
            scheduleId,
            scheduleAddress:
              item.schedule.address ??
              item.schedule.location?.name ??
              "",
            locationName:
              item.schedule.address ??
              item.schedule.location?.name ??
              "",
            services,
            time,
            date: item.date as DbDateString,
            // The zone these openings were computed in, so the confirmation's
            // calendar event lands on the business's clock (BF-DRIFT-016).
            timezone: item.timezone,
            cancellationPolicyEnabled,
            cancellationPolicyHours,
            cancellationPolicyNote,
            lateGraceMinutes,
            verificationMethod,
          });
        };

        return (
          <Flex
            key={scopeKey}
            vertical
            gap={8}
            style={{ width: "100%" }}
            data-next-opening-scope={scopeKey}
          >
            <Flex align="baseline" gap={8} wrap="wrap">
              <Text style={bookingSectionTitleStyle}>{dateLabel}</Text>
              {scheduleLabel ? (
                <span style={bookingSmallStyle}>· {scheduleLabel}</span>
              ) : null}
            </Flex>

            {scheduleId ? (
              showAvailabilityStatus ? (
                <TimeGrid
                  times={item.openings as DbTimeString[]}
                  resetKey={`${selectionContextKey}:${scopeKey}`}
                  selectedTime={
                    selectedOpening?.scopeKey === scopeKey
                      ? selectedOpening.time
                      : null
                  }
                  onSelectedTimeChange={(time) =>
                    setSelectedOpening({ scopeKey, time })
                  }
                  getLabel={(time) =>
                    item.openingLabels?.[time] ??
                    formatCompactTime(time, {
                      uppercase: true,
                      alwaysShowMinutes: true,
                    })
                  }
                  onSelect={handleSelect}
                />
              ) : (
                <Row gutter={[8, 8]}>
                  {item.openings.map((time, index) => {
                    const dbTime = time as DbTimeString;
                    const label =
                      item.openingLabels?.[dbTime] ??
                      formatCompactTime(dbTime, {
                        uppercase: true,
                        alwaysShowMinutes: true,
                      });
                    return (
                      <Col key={`${time}-${index}`} span={8}>
                        <TimeButton
                          onClick={() => handleSelect(dbTime)}
                          label={label}
                          time={dbTime}
                        />
                      </Col>
                    );
                  })}
                </Row>
              )
            ) : null}
          </Flex>
        );
      })}
    </Flex>
  ) : (
    <Flex vertical style={availabilityPromptStyle}>
      <Paragraph
        className="openings-booking-empty-message"
        style={emptyStateTextStyle}
      >
        No openings found for this date. Check availability on other days.
      </Paragraph>
      <Button
        block
        className="openings-booking-primary-action"
        color="default"
        variant="solid"
        icon={<CalendarOutlined />}
        type="primary"
        onClick={onSearch}
        style={bookingPrimaryActionStyle}
      >
        Next Availability
      </Button>
    </Flex>
  );
};

export default DisplayNextOpenings;
